/**
 * ReviewQueueRowView — pure presentation for one pipeline Review-queue row
 * (SE-5499, restyled SE-6554).
 *
 * The design's four-row card:
 *   1. origin badge + attribution + age
 *   2. avatar + player name + the proposed transition
 *   3. signal chips
 *   4. Accept / Decline
 *
 * Pending rows expose the two inline actions (not a hidden kebab menu).
 * Resolved rows drop the actions and carry a resolved badge instead, so an
 * accepted/declined item stays visible (AC4) — the design frame only ever draws
 * the pending state, because everything in it is awaiting a decision.
 *
 * ## Why the pills come from `TonePill`
 *
 * Every pill here is a design-system pill, not hand-rolled chrome: the pill
 * radius, fill and label colour all live inside the Badge atom's closed tone
 * map. That is also what keeps this file out of the `roundedFullFeatureViews`
 * budget, which counts feature Views that spell pill chrome themselves.
 * @remarks View component — no business logic, hooks, or state.
 */

import { memo } from "react";

import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import {
  Badge,
  BadgeText,
  TONE_CLASS,
  TonePill,
  type PillTone,
} from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link ReviewQueueRowView}. */
export interface ReviewQueueRowViewProps {
  /** Origin label (rule name or source fallback). */
  readonly ruleName: string;
  /** Resolved player display name (null while the per-row query loads). */
  readonly playerName: string | null;
  /** Whether the per-row player query is loading. */
  readonly playerLoading: boolean;
  /** Stage the card is in now. */
  readonly fromPhaseName: string | null;
  /** Proposed target stage. */
  readonly targetPhaseName: string | null;
  /** Human-readable source label (e.g. "Scout flag"). */
  readonly sourceLabel: string;
  /** SE-6554: short origin-badge label ("Rule" / "Scout" / "Manual"). */
  readonly originLabel: string;
  /** SE-6554: origin-badge tone pairing. */
  readonly originTone: PillTone;
  /** SE-6554: who/what asked, in the producer's words where there are any. */
  readonly attribution: string;
  /** SE-6554: the proposed move as one line, or the flagged-only wording. */
  readonly transitionLabel: string;
  /** SE-6554: compact request age ("2d ago"); "" renders nothing. */
  readonly ageLabel: string;
  /** SE-6554: player position + team chip; "" renders nothing. */
  readonly contextLabel: string;
  /** SE-6554: proposed terminal outcome; "" renders nothing. */
  readonly outcomeLabel: string;
  /** Human-readable status label (Pending / Accepted / Declined). */
  readonly statusLabel: string;
  /** Badge tone for the status. */
  readonly statusTone: "muted" | "success" | "error";
  /** Whether the row is still awaiting a decision. */
  readonly isPending: boolean;
  /** Whether the row has been accepted or declined. */
  readonly isResolved: boolean;
  /** Whether an accept/decline is in flight for this row. */
  readonly isDeciding: boolean;
  /** Accept handler. */
  readonly onAccept: () => void;
  /** Decline handler. */
  readonly onDecline: () => void;
  /** testID for the row container. */
  readonly testID?: string;
}

/**
 * Render one signal chip. A render helper, not a component — the same shape
 * `StageEditorView` and `PipelineChipsRowView` use, so a component directory
 * still holds exactly its index/Container/View trio.
 *
 * Square-cornered, unlike the origin badge above it: the design separates "what
 * produced this" from "what is true about the player" by shape as well as
 * colour.
 * @param label - Chip text.
 * @param tone - Tone pairing from the Badge atom's closed map.
 * @param testID - Test identity.
 * @returns The chip node.
 */
const renderSignalChip = (label: string, tone: PillTone, testID: string) => (
  <Box
    testID={testID}
    className={`rounded-sm px-2 py-micro ${TONE_CLASS[tone].box}`}
  >
    <Text variant="micro" className={TONE_CLASS[tone].text}>
      {label}
    </Text>
  </Box>
);

/**
 * Render one Review-queue row.
 * @param props - {@link ReviewQueueRowViewProps}
 * @returns The row card.
 */
const ReviewQueueRowView = (props: ReviewQueueRowViewProps) => (
  <Box
    testID={props.testID}
    className="gap-2 rounded-md border border-outline-subtle bg-surface-base px-3 py-2"
  >
    {/* Row 1 — origin badge, attribution, age. */}
    <Stack direction="horizontal" className="items-center gap-2">
      <TonePill
        tone={props.originTone}
        label={props.originLabel}
        textVariant="micro"
        className="shrink-0 px-2 py-micro"
        testID="review-queue-row-origin"
      />
      <Text
        variant="micro"
        className="min-w-0 flex-1 text-content-muted"
        numberOfLines={1}
      >
        {props.attribution}
      </Text>
      {props.ageLabel === "" ? null : (
        <Text variant="micro" className="shrink-0 text-content-muted">
          {props.ageLabel}
        </Text>
      )}
    </Stack>

    {/* Row 2 — identity and the proposed move. The design's GPR pill sits at
        this row's right edge; no GPR reaches this surface, so the slot carries
        the resolved badge instead and is empty while a row is still pending
        (the Accept/Decline pair below already says "pending"). */}
    <Stack direction="horizontal" className="items-center gap-2">
      <Avatar size="xs" className="shrink-0 bg-surface-inverse">
        <AvatarFallbackText className="text-content-inverse">
          {props.playerName ?? "?"}
        </AvatarFallbackText>
      </Avatar>

      <Stack className="min-w-0 flex-1 gap-px">
        {props.playerLoading && props.playerName === null ? (
          <Skeleton className="h-5 w-40 rounded-sm" />
        ) : (
          <Text
            variant="body-strong"
            className="text-content-primary"
            numberOfLines={1}
          >
            {props.playerName ?? "Unknown player"}
          </Text>
        )}
        <Text
          variant="caption"
          className="text-content-primary"
          numberOfLines={1}
        >
          {props.transitionLabel}
        </Text>
      </Stack>

      {props.isResolved ? (
        <Badge action={props.statusTone} variant="solid">
          <BadgeText>{props.statusLabel}</BadgeText>
        </Badge>
      ) : null}
    </Stack>

    {/* Row 3 — signals. The design's GPR / "N reports" / "Starting XI" /
        "Youth" set has no source on this surface (see the Container's note), so
        this row renders only the two signals the queue's own reads carry, and
        disappears entirely when a row carries neither. */}
    {props.contextLabel === "" && props.outcomeLabel === "" ? null : (
      <Stack direction="horizontal" className="flex-wrap items-center gap-2">
        {props.contextLabel === ""
          ? null
          : renderSignalChip(
              props.contextLabel,
              "neutral-raised",
              "review-queue-row-context"
            )}
        {props.outcomeLabel === ""
          ? null
          : renderSignalChip(
              `Outcome: ${props.outcomeLabel}`,
              "info-soft",
              "review-queue-row-outcome"
            )}
      </Stack>
    )}

    {/* Row 4 — the decision. Accept leads, as the design orders it. */}
    {props.isPending ? (
      <Stack direction="horizontal" className="items-center gap-2">
        {/* tone="accent", never solid `primary` — primary solid is WHITE in
            dark mode, which is not a CTA. */}
        <Button
          label="Accept"
          variant="solid"
          tone="accent"
          size="sm"
          className="h-8 flex-1"
          onPress={props.onAccept}
          isDisabled={props.isDeciding}
          isLoading={props.isDeciding}
          accessibilityLabel={`Accept ${props.sourceLabel} request`}
          testID="review-queue-row-accept"
        />
        <Button
          label="Decline"
          variant="outline"
          tone="secondary"
          size="sm"
          labelTone="muted"
          className="h-8 flex-1"
          onPress={props.onDecline}
          isDisabled={props.isDeciding}
          isLoading={props.isDeciding}
          accessibilityLabel={`Decline ${props.sourceLabel} request`}
          testID="review-queue-row-decline"
        />
      </Stack>
    ) : null}
  </Box>
);

ReviewQueueRowView.displayName = "ReviewQueueRowView";

export default memo(ReviewQueueRowView);
